import type { QueryClient } from "@tanstack/react-query";
import type { HistoryState } from "@tanstack/react-router";
import { type DailyLogSearchRouteParams, parseSearchConditions, toRouteSearch } from "./dailyLogSearchState";

/**
 * 검색 목록 → 일지 상세 → 목록 왕복에 필요한 복귀 정보와 위치 메타데이터.
 * 정본: TRD §6.6
 *
 * 원본 본문·발췌·응답 전체는 저장하지 않는다. 식별자와 위치만 저장한다.
 */

/** 목록에서 상세로 push할 때 history state로 넘기는 복귀 정보 */
export interface DailyLogSearchReturnState {
  projectId: string;
  /** 허용된 검색 키만 담은 목록 조건 */
  search: DailyLogSearchRouteParams;
  /** 목록 history entry의 `__TSR_index`. 닫기 버튼이 원래 entry로 돌아갈 때 쓴다 */
  listHistoryIndex: number;
}

declare module "@tanstack/react-router" {
  interface HistoryState {
    dailyLogSearchReturn?: DailyLogSearchReturnState;
    /** Work History 탭으로 갈 때 넘기는 Documents 검색 조건. Work History 집계에는 쓰지 않는다 */
    projectDocumentsSearch?: DailyLogSearchRouteParams;
  }
}

/**
 * Work History에서 Documents 탭으로 돌아갈 때 쓸 검색 조건.
 * history state의 값을 그대로 믿지 않고 허용된 검색 키로만 다시 만든다. 없거나 잘못되면 기본 목록.
 */
export function sanitizeDocumentsSearch(search: DailyLogSearchRouteParams | undefined): DailyLogSearchRouteParams {
  if (!search) return {};
  const { conditions, issues } = parseSearchConditions(search);
  return issues.length === 0 ? withoutUndefined(toRouteSearch(conditions)) : {};
}

export function createDailyLogSearchReturnState(
  projectId: string | number,
  search: DailyLogSearchRouteParams,
  listHistoryIndex: number,
): DailyLogSearchReturnState {
  return { projectId: String(projectId), search: withoutUndefined(search), listHistoryIndex };
}

export type ResolvedDailyLogSearchReturn =
  /** 같은 탭에서 검색 목록으로부터 들어왔다. 원래 목록 entry로 `history.go(delta)` */
  | { kind: "history"; delta: number }
  /** 앱 안의 다른 화면(홈 최근 일지, Work History 등)에서 들어왔다. 직전 화면으로 돌아간다 */
  | { kind: "back" }
  /** 직접 열기·새 탭처럼 앱 안의 이전 화면이 없다. 이 프로젝트의 목록으로 이동한다 */
  | { kind: "navigate"; search: DailyLogSearchRouteParams };

/**
 * 상세 화면의 닫기 동작을 결정한다.
 * 임의 문자열 URL은 받지 않고, 같은 프로젝트의 허용된 검색 키만 재구성한다.
 *
 * 앱 안에서 들어왔는지는 TanStack history의 `__TSR_index`로 판단한다. 앱이 이 탭에서 처음 연 entry는 0이라
 * 직접 URL·새 탭은 0이고, 앱 안에서 push한 entry는 1 이상이다. `window.history.length`는 외부 페이지까지 세므로 쓰지 않는다.
 */
export function resolveDailyLogSearchReturn(
  state: (HistoryState & { __TSR_index?: number }) | undefined,
  currentProjectId: string,
): ResolvedDailyLogSearchReturn {
  const currentIndex = state?.__TSR_index;
  const hasInAppPreviousEntry = typeof currentIndex === "number" && currentIndex > 0;
  const returnState = state?.dailyLogSearchReturn;

  if (!returnState || returnState.projectId !== currentProjectId) {
    return hasInAppPreviousEntry ? { kind: "back" } : { kind: "navigate", search: {} };
  }

  const { conditions, issues } = parseSearchConditions(returnState.search ?? {});
  const search = issues.length === 0 ? withoutUndefined(toRouteSearch(conditions)) : {};

  const listIndex = returnState.listHistoryIndex;
  const canGoBackToList =
    Number.isInteger(listIndex) && typeof currentIndex === "number" && 0 <= listIndex && listIndex < currentIndex;

  if (canGoBackToList) return { kind: "history", delta: listIndex - currentIndex };
  return { kind: "navigate", search };
}

function withoutUndefined(search: DailyLogSearchRouteParams): DailyLogSearchRouteParams {
  return Object.fromEntries(
    Object.entries(search).filter(([, value]) => value !== undefined),
  ) as DailyLogSearchRouteParams;
}

/* ------------------------------------------------------------------ */
/* 위치 메타데이터                                                       */
/* ------------------------------------------------------------------ */

export interface DailyLogSearchNavigationEntry {
  version: 1;
  userId: number;
  projectId: number;
  /** 정규화된 목록 조건. 같은 entry라도 조건이 다르면 적용하지 않는다 */
  listSearchKey: string;
  historyKey: string;
  selectedLogId: number | null;
  anchorLogId: number | null;
  anchorDate: string | null;
  anchorOffsetPx: number;
  scrollTop: number;
  loadedPageCount: number;
  /** anchor에 가까운 순서의 주변 행 ID. anchor가 사라졌을 때 대체 기준 */
  nearbyLogIds: number[];
  updatedAt: number;
}

export const NAVIGATION_ENTRY_TTL_MS = 1000 * 60 * 30;
export const NAVIGATION_ENTRY_LIMIT = 20;
const STORAGE_KEY = "bricks.daily-log-search.navigation.v1";

/** sessionStorage를 쓸 수 없을 때(사파리 프라이빗 모드, 용량 초과)의 대체 저장소 */
const memoryEntries = new Map<string, DailyLogSearchNavigationEntry>();

function entryId(userId: number, projectId: number, historyKey: string) {
  return `${userId}:${projectId}:${historyKey}`;
}

export function toListSearchKey(search: DailyLogSearchRouteParams): string {
  const { conditions } = parseSearchConditions(search);
  return JSON.stringify(conditions);
}

function isNavigationEntry(value: unknown): value is DailyLogSearchNavigationEntry {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as Partial<DailyLogSearchNavigationEntry>;
  return (
    entry.version === 1 &&
    typeof entry.userId === "number" &&
    typeof entry.projectId === "number" &&
    typeof entry.historyKey === "string" &&
    typeof entry.listSearchKey === "string" &&
    typeof entry.loadedPageCount === "number" &&
    typeof entry.updatedAt === "number" &&
    Array.isArray(entry.nearbyLogIds)
  );
}

function readStoredEntries(): Record<string, DailyLogSearchNavigationEntry> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return {};
    return Object.fromEntries(Object.entries(parsed).filter(([, value]) => isNavigationEntry(value))) as Record<
      string,
      DailyLogSearchNavigationEntry
    >;
  } catch {
    // parse 실패·저장소 접근 불가는 화면 오류가 아니다. 메모리 값만 쓴다.
    return {};
  }
}

function pruneEntries(entries: DailyLogSearchNavigationEntry[], now: number) {
  return entries
    .filter((entry) => now - entry.updatedAt < NAVIGATION_ENTRY_TTL_MS)
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, NAVIGATION_ENTRY_LIMIT);
}

export function saveNavigationEntry(entry: DailyLogSearchNavigationEntry, now: number = Date.now()): void {
  const id = entryId(entry.userId, entry.projectId, entry.historyKey);

  memoryEntries.set(id, entry);
  const keptMemory = new Set(pruneEntries([...memoryEntries.values()], now));
  for (const [key, stored] of memoryEntries) {
    if (!keptMemory.has(stored)) memoryEntries.delete(key);
  }

  const kept = pruneEntries(Object.values({ ...readStoredEntries(), [id]: entry }), now);
  try {
    const next = Object.fromEntries(
      kept.map((stored) => [entryId(stored.userId, stored.projectId, stored.historyKey), stored]),
    );
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // 용량 초과 등은 메모리 복원으로 대체한다.
  }
}

export function loadNavigationEntry(
  userId: number,
  projectId: number,
  historyKey: string,
  now: number = Date.now(),
): DailyLogSearchNavigationEntry | null {
  const id = entryId(userId, projectId, historyKey);
  const entry = readStoredEntries()[id] ?? memoryEntries.get(id) ?? null;
  if (!entry || now - entry.updatedAt >= NAVIGATION_ENTRY_TTL_MS) return null;
  return entry;
}

/**
 * 권한 회수·사용자 변경 시 해당 범위의 복귀 메타데이터를 지운다.
 * `exceptUserId`를 주면 그 사용자를 뺀 나머지(이전 사용자) 전부를 지운다.
 */
export function clearNavigationEntries(
  match: { userId?: number; projectId?: number; exceptUserId?: number } = {},
): void {
  const shouldRemove = (entry: DailyLogSearchNavigationEntry) =>
    (match.userId === undefined || entry.userId === match.userId) &&
    (match.projectId === undefined || entry.projectId === match.projectId) &&
    (match.exceptUserId === undefined || entry.userId !== match.exceptUserId);

  for (const [key, entry] of memoryEntries) {
    if (shouldRemove(entry)) memoryEntries.delete(key);
  }
  try {
    const remaining = Object.entries(readStoredEntries()).filter(([, entry]) => !shouldRemove(entry));
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(remaining)));
  } catch {
    // 저장소를 쓸 수 없으면 메모리만 정리한다.
  }
}

/* ------------------------------------------------------------------ */
/* 스크롤 anchor                                                        */
/* ------------------------------------------------------------------ */

const ROW_SELECTOR = "[data-daily-log-id]";
const NEARBY_ROW_COUNT = 5;

export interface ScrollAnchor {
  anchorLogId: number | null;
  anchorDate: string | null;
  anchorOffsetPx: number;
  scrollTop: number;
  nearbyLogIds: number[];
}

function readRowId(row: Element): number {
  return Number((row as HTMLElement).dataset.dailyLogId);
}

/** 화면 상단에 걸친 첫 행을 anchor로 잡는다. 클릭한 행이 아니다 */
export function captureScrollAnchor(container: HTMLElement): ScrollAnchor {
  const rows = Array.from(container.querySelectorAll(ROW_SELECTOR));
  const containerTop = container.getBoundingClientRect().top;
  const anchorIndex = rows.findIndex((row) => row.getBoundingClientRect().bottom > containerTop);

  if (anchorIndex < 0) {
    return { anchorLogId: null, anchorDate: null, anchorOffsetPx: 0, scrollTop: container.scrollTop, nearbyLogIds: [] };
  }

  const anchorRow = rows[anchorIndex] as HTMLElement;
  return {
    anchorLogId: readRowId(anchorRow),
    anchorDate: anchorRow.dataset.dailyLogDate ?? null,
    anchorOffsetPx: anchorRow.getBoundingClientRect().top - containerTop,
    scrollTop: container.scrollTop,
    nearbyLogIds: orderByDistance(rows, anchorIndex).map(readRowId),
  };
}

/** anchor 기준 가까운 순서: +1, -1, +2, -2 … */
function orderByDistance(rows: Element[], anchorIndex: number): Element[] {
  const ordered: Element[] = [];
  for (let distance = 1; distance <= NEARBY_ROW_COUNT; distance++) {
    const after = rows[anchorIndex + distance];
    const before = rows[anchorIndex - distance];
    if (after) ordered.push(after);
    if (before) ordered.push(before);
  }
  return ordered;
}

export type AnchorRestoreResult =
  | { restored: true; method: "anchor" | "nearby" | "date" | "scroll-top"; logId: number | null }
  | { restored: false };

/**
 * 저장한 anchor 위치로 스크롤을 맞춘다.
 * anchor가 삭제·필터 탈락했으면 주변 ID → date/id 근처 → 유효 범위의 scrollTop 순으로 대체한다.
 */
export function restoreScrollAnchor(container: HTMLElement, anchor: ScrollAnchor): AnchorRestoreResult {
  const rows = Array.from(container.querySelectorAll(ROW_SELECTOR)) as HTMLElement[];
  if (rows.length === 0) return { restored: false };

  const findRow = (id: number | null) => (id === null ? undefined : rows.find((row) => readRowId(row) === id));

  const anchorRow = findRow(anchor.anchorLogId);
  if (anchorRow) {
    scrollRowTo(container, anchorRow, anchor.anchorOffsetPx);
    return { restored: true, method: "anchor", logId: readRowId(anchorRow) };
  }

  for (const nearbyId of anchor.nearbyLogIds) {
    const nearbyRow = findRow(nearbyId);
    if (nearbyRow) {
      scrollRowTo(container, nearbyRow, anchor.anchorOffsetPx);
      return { restored: true, method: "nearby", logId: nearbyId };
    }
  }

  // 목록은 date DESC, id DESC 정렬이다. 저장한 anchor 바로 뒤에 올 행을 찾는다.
  if (anchor.anchorDate !== null && anchor.anchorLogId !== null) {
    const { anchorDate, anchorLogId } = anchor;
    const followingRow = rows.find((row) => {
      const date = row.dataset.dailyLogDate ?? "";
      return date < anchorDate || (date === anchorDate && readRowId(row) < anchorLogId);
    });
    if (followingRow) {
      scrollRowTo(container, followingRow, 0);
      return { restored: true, method: "date", logId: readRowId(followingRow) };
    }
  }

  const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
  container.scrollTop = Math.min(anchor.scrollTop, maxScrollTop);
  return { restored: true, method: "scroll-top", logId: null };
}

function scrollRowTo(container: HTMLElement, row: HTMLElement, offsetPx: number) {
  const rowTop = row.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
  container.scrollTop = rowTop - offsetPx;
}

/** 복귀 후 선택했던 일지 링크로 초점을 돌린다. 사용자가 이미 다른 곳을 조작 중이면 빼앗지 않는다 */
export function focusRowLink(container: HTMLElement, logId: number | null): void {
  if (logId === null) return;
  const isUserFocusedElsewhere = document.activeElement !== null && document.activeElement !== document.body;
  if (isUserFocusedElsewhere) return;

  const link = container.querySelector<HTMLElement>(`[data-daily-log-id="${logId}"] a`);
  link?.focus({ preventScroll: true });
}

/**
 * 로그인 사용자가 바뀌면(공용 기기에서 다른 작업자가 로그인) 이전 사용자의 보호 검색 데이터를 지운다.
 * query key에 사용자 ID가 있어 화면에 섞이지는 않지만, 메모리 캐시와 세션 저장소에 남기지 않는다.
 */
export function clearDailyLogSearchDataOfOtherUsers(queryClient: QueryClient, currentUserId: number): void {
  queryClient.removeQueries({
    queryKey: ["daily-log-search"],
    predicate: (query) => query.queryKey[1] !== currentUserId,
  });
  clearNavigationEntries({ exceptUserId: currentUserId });
}
